import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultSelectStyles } from '@jira/issue__field-select-indicator-fix-styles/src/indicatorFixSelectStyles.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { useProjectContext } from '@jira/platform__services-providers-project-context';
import SmartUserPicker from '@atlaskit/smart-user-picker/components';
import type { OnChange } from '@atlaskit/smart-user-picker/user-picker';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { NO_TEAM_ID } from './noTeamId.constants.tsx';
import { fetchTeamsFilter } from './fetchTeamsFilter.tsx';
import { CreateTeamOption } from './CreateTeamOption.tsx';
import { TeamPickerEmptyState } from './TeamPickerEmptyState.tsx';
import { VerifiedTeamFooter } from './VerifiedTeamFooter.tsx';
import type { AsyncTeamPickerEditEntryPointProps } from './AsyncTeamPickerEdit.entrypoint.types.tsx';

// FIXME: These are overrides for @atlaskit/select@15.6 -> react-select@4.3.x
//        Once @atlaskit/select@15.6 resolution has been removed from package.json
//        these should be removed as well. See PTC-6238.
const stylesOverrides = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	valueContainer: (base: any) => ({ ...base, display: 'grid' }),
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	placeholder: (base: any) => ({
		...base,
		position: undefined,
		top: undefined,
		left: undefined,
		transform: undefined,
	}),
};

const placeholderTeamEditSelectorMessage = defineMessage({
	id: 'issue-field-team.placeholder-team-edit-selector',
	defaultMessage: 'Choose a team',
	description: 'A placeholder shown in the team field while no value is selected',
});

const defaultNoValueTextMessage = defineMessage({
	id: 'issue-field-team.default-no-value-text',
	defaultMessage: 'None',
	description: 'The default text displayed on the no team option',
});
export const TeamPickerEdit = (props: AsyncTeamPickerEditEntryPointProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		siteId,
		defaultValue,
		isClearable = true,
		isDisabled = false,
		isInvalid = false,
		autoFocus = false,
		width = '100%',
		height,
		debounceTime = 300,
		onChange,
		onBlur,
		placeholder = formatMessage(placeholderTeamEditSelectorMessage),
		isDropdownMenuFixedAndLayered = false,
		isMenuPositionFixed = false,
		styles,
		noValueText,
		createTeamDialogTrigger,
		isCompact = true,
		hasNoTeamOption = true,
		description,
		name,
		menuPortalTarget,
		ariaDescribedBy,
		...fieldProps
	} = props;

	const [isCreateButtonFocused, setIsCreateButtonFocused] = useState(false);

	const { data: orgId } = useOrgId();

	const [isFooterFocused, setIsFooterFocused] = useState(false);

	const [hasTeams, setHasTeams] = useState(true);

	const [teamsFilter, setTeamsFilter] = useState<string | null>(null);

	const isManagedTeamsEnabled = useMemo(() => {
		if (!fg('enable_filter_settings_for_teams_field')) return false;
		return teamsFilter === 'ONLY_VERIFIED';
	}, [teamsFilter]);

	const formattedNoValueText = formatMessage(defaultNoValueTextMessage);

	const [updatedPlaceholder, setUpdatedPlaceholder] = useState(placeholder);
	const [inputQuery, setInputQuery] = useState('');

	const finalStyles = {
		...defaultSelectStyles,
		...(styles || {}),
		...stylesOverrides,
	};

	const { data: project } = useProjectContext();

	useEffect(() => {
		if (name && siteId && fg('enable_filter_settings_for_teams_field')) {
			const projectId =
				project?.projectId && fg('_managed_team_graphql_fix_ken_4267')
					? JiraProjectAri.create({
							siteId,
							projectId: project.projectId.toString(),
						}).toString()
					: '';
			fetchTeamsFilter(name, siteId, projectId).then(setTeamsFilter);
		}
	}, [name, siteId, project?.projectId]);

	const maybeAddNoTeamOption = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'users' implicitly has an 'any' type.
		(users, query: string) => {
			setHasTeams(!(users.length === 0 && query.length === 0));

			if (query === '' && !!defaultValue && hasNoTeamOption) {
				return [
					{
						id: NO_TEAM_ID,
						name: noValueText ?? formattedNoValueText,
						isVerified: false,
						type: 'team',
					},
				].concat(users);
			}
			setIsCreateButtonFocused(query.length > 0 && users.length === 0);

			return users;
		},
		[defaultValue, noValueText, formattedNoValueText, hasNoTeamOption],
	);

	const handleOnChange: OnChange = (newValue, actionType) => {
		if (Array.isArray(newValue)) return;

		if (onChange) {
			onChange(newValue?.id === NO_TEAM_ID || !newValue ? null : newValue, actionType);
			setUpdatedPlaceholder(
				newValue?.id === NO_TEAM_ID || !newValue ? formattedNoValueText : placeholder,
			);
		}
	};

	const handleCreateTeam = useCallback(() => {
		createTeamDialogTrigger(inputQuery);
	}, [inputQuery, createTeamDialogTrigger]);

	const handleOnKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (!isManagedTeamsEnabled && isCreateButtonFocused && event.key === 'Enter') {
				handleCreateTeam();
			}
		},
		[isManagedTeamsEnabled, isCreateButtonFocused, handleCreateTeam],
	);

	const handleOnInputChange = useCallback(
		(query?: string) => {
			setInputQuery(query || '');
		},
		[setInputQuery],
	);

	const footer = useMemo(
		() =>
			hasTeams &&
			(isManagedTeamsEnabled ? (
				<VerifiedTeamFooter />
			) : (
				<CreateTeamOption
					isFocused={isCreateButtonFocused}
					onClick={handleCreateTeam}
					setIsFooterFocused={setIsFooterFocused}
				/>
			)),
		[hasTeams, handleCreateTeam, isCreateButtonFocused, isManagedTeamsEnabled],
	);

	const noOptionsMessage = useMemo(
		() => (!hasTeams ? () => <TeamPickerEmptyState onActionClick={handleCreateTeam} /> : undefined),
		[hasTeams, handleCreateTeam],
	);

	const menuListStyle = useMemo(
		() =>
			!hasTeams
				? {
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						menuList: (base: any) => ({
							...base,
							maxHeight: '350px',
						}),
					}
				: {},
		[hasTeams],
	);

	const getMenuPosition = () => {
		if (isMenuPositionFixed) {
			return 'fixed';
		}
		return isDropdownMenuFixedAndLayered ? 'absolute' : undefined;
	};

	return (
		<SmartUserPicker
			// Setting width=100% will allow dropdown to size correctly
			width={width}
			height={height}
			autoFocus={autoFocus}
			appearance={isCompact ? 'compact' : 'normal'}
			debounceTime={debounceTime}
			fieldId="team"
			filterOptions={maybeAddNoTeamOption}
			footer={footer}
			includeGroups={false}
			includeTeams
			includeUsers={false}
			isClearable={isClearable}
			isMulti={false}
			isDisabled={isDisabled}
			isInvalid={isInvalid}
			// Stop listbox auto-opening on focus (WCAG 3.2.1). See https://hello.jira.atlassian.cloud/jira/browse/A11Y-26553
			{...(expValEquals('ai-a11y-exp-26553-open-on-focus', 'cohort', 'test')
				? { openMenuOnFocus: false }
				: {})}
			productKey="jira"
			siteId={siteId}
			value={defaultValue}
			onChange={handleOnChange}
			onBlur={onBlur}
			orgId={orgId}
			placeholder={updatedPlaceholder}
			menuPosition={getMenuPosition()}
			menuPortalTarget={menuPortalTarget}
			styles={{ ...finalStyles, ...menuListStyle }}
			onInputChange={handleOnInputChange}
			onKeyDown={handleOnKeyDown}
			noOptionsMessage={noOptionsMessage}
			ariaLabel={`${updatedPlaceholder} ${description}`}
			ariaLabelledBy={fieldProps['aria-labelledby']}
			{...(ariaDescribedBy ? { ariaDescribedBy } : {})}
			placeholderAvatar="team"
			{...(fg('a11y-create-team-is-not-focusable-and-has-no-btn') && { setIsFooterFocused })}
			{...(fg('a11y-create-team-is-not-focusable-and-has-no-btn') && { isFooterFocused })}
			{...(isManagedTeamsEnabled && { verifiedTeams: true })}
		/>
	);
};

export const TeamPickerEditEntryPointContent = ({
	props,
}: {
	props: AsyncTeamPickerEditEntryPointProps;
}): React.JSX.Element => <TeamPickerEdit {...props} />;

export default TeamPickerEditEntryPointContent;
